import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useAnalyticsStatsContextOrDefault } from '~/views/Dashboard/AnalyticsStats/AnalyticsProvider';
import {
  analyticsAuthoringQueryKeyPart,
  analyticsQueryParams,
  type AnalyticsMode,
} from '~/views/Dashboard/AnalyticsStats/analyticsMode';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import { ErrorObjectString } from '~/utils/typescriptHelpers';

export const columnRefSchema = z.union([
  z.object({ kind: z.literal('grouping'), key: z.string() }),
  z.object({ kind: z.literal('stat'), alias: z.string() }),
  z.object({ kind: z.literal('display'), alias: z.string() }),
]);

export type ColumnRef = z.infer<typeof columnRefSchema>;

const statsViewSchema = z.object({
  publicId: z.string(),
  statKey: z.string(),
  viewType: z.union([z.literal('limit'), z.literal('custom')]),
  isAppliedLimit: z.literal(true).optional(),
  accountOwnerType: z
    .union([z.literal('client'), z.literal('partner')])
    .nullable()
    .optional(),
  accountOwnerId: z.number().nullable().optional(),
  accountScopeMode: z.union([z.literal('exact'), z.literal('children')]).optional(),
  userOwnerId: z.number().nullable().optional(),
  isTemplate: z.boolean().default(false),
  sourceTemplatePublicId: z.string().nullable().optional(),
  selection: z.object({
    columnLayout: z.array(columnRefSchema).nullable(),
  }),
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  scopeBucket: z
    .union([
      z.literal('userGlobal'),
      z.literal('userAccount'),
      z.literal('accountExact'),
      z.literal('parentExact'),
      z.literal('parentChildren'),
      z.literal('other'),
    ])
    .optional(),
});

const statsViewsResponseSchema = z.object({
  views: z.array(statsViewSchema),
});

export type AnalyticsStatsView = z.infer<typeof statsViewSchema>;
export type AnalyticsStatsViewsResponse = z.infer<typeof statsViewsResponseSchema>;

export const isAssignedTemplate = (view: AnalyticsStatsView): boolean =>
  view.viewType === 'custom' && view.isTemplate === true;

export const isCustomReport = (view: AnalyticsStatsView): boolean =>
  view.viewType === 'custom' && !view.isTemplate;

type ResponseData = AnalyticsStatsViewsResponse | ErrorObjectString;

const getAnalyticsStatsViews = async (
  accountId: number,
  accountType: 'client' | 'partner',
  mode?: AnalyticsMode,
  authoringStatSet?: Genie2ReportingMode | null,
): Promise<AnalyticsStatsViewsResponse> => {
  const resolvedMode = mode ?? 'client';
  const { data } = await customFetch.get<ResponseData>(
    `/authenticated/${accountType}/${accountId}/analytics/stats/views`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      useNodeEndpoint: true,
      params: analyticsQueryParams(resolvedMode, authoringStatSet),
    },
  );

  if ('error' in data) {
    throw new Error(data.error);
  }

  return statsViewsResponseSchema.parse(data);
};

export const getAnalyticsStatsViewsQueryOptions = (
  accountId: number,
  accountType: 'client' | 'partner',
  mode?: AnalyticsMode,
  authoringStatSet?: Genie2ReportingMode | null,
) => {
  const resolvedMode = mode ?? 'client';
  return queryOptions({
    queryKey: [
      'analytics-stats',
      'views',
      accountId,
      accountType,
      resolvedMode,
      analyticsAuthoringQueryKeyPart(resolvedMode, authoringStatSet),
    ],
    queryFn: () => getAnalyticsStatsViews(accountId, accountType, resolvedMode, authoringStatSet),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAnalyticsStatsViewsQuery = () => {
  const { accountId, accountType, mode, authoringStatSet } = useAnalyticsStatsContextOrDefault();

  return useQuery({
    ...getAnalyticsStatsViewsQueryOptions(
      accountId,
      accountType,
      mode,
      mode === 'authoring' ? authoringStatSet : undefined,
    ),
    enabled: !!accountId,
    placeholderData: keepPreviousData,
  });
};
